import { Text, type TextProps } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { type Theme } from "@mui/material";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import pluralize from "pluralize";

interface LateCancellationTextProps extends Omit<TextProps, "color"> {
  readonly lateCancellationPeriod: number;
  readonly lateCancellationFeeHours: number;
  readonly color?: (theme: Theme) => string | undefined;
}

export function LateCancellationText(props: LateCancellationTextProps) {
  const {
    lateCancellationPeriod,
    lateCancellationFeeHours,
    variant = "body2",
    color: propsColor,
    ...textProps
  } = props;

  const isUnifyLateCancellationPolicyAndPayoutEnabled = useCbhFlag(
    CbhFeatureFlag.UNIFY_LATE_CANCELLATION_POLICY_AND_PAYOUT,
    {
      defaultValue: false,
    }
  );

  const workerHasLateCancelProtection = isUnifyLateCancellationPolicyAndPayoutEnabled;

  const hasLateCancellationPayout = lateCancellationPeriod > 0 && lateCancellationFeeHours > 0;

  const lateCancellationPeriodText = `${lateCancellationPeriod} ${pluralize(
    "hour",
    lateCancellationPeriod
  )}`;
  const lateCancellationFeeHoursText = `${lateCancellationFeeHours} ${pluralize(
    "hour",
    lateCancellationFeeHours
  )}`;

  return (
    <Text
      variant={variant}
      color={(theme) => {
        if (isDefined(propsColor)) {
          return propsColor(theme);
        }

        return theme.palette.text.secondary;
      }}
      {...textProps}
    >
      {workerHasLateCancelProtection && (
        <>
          Shifts cancelled by the workplace within 2 hours of the shift are covered by{" "}
          <strong>Late Cancel Protection</strong> — you are guaranteed a{" "}
          <strong>2 hours payout</strong>.
        </>
      )}

      {!workerHasLateCancelProtection && hasLateCancellationPayout && (
        <>
          Shifts cancelled by this workplace within <strong>{lateCancellationPeriodText}</strong>{" "}
          before shift start will lead to a <strong>{lateCancellationFeeHoursText}</strong> payout
          for the scheduled worker.
        </>
      )}

      {!workerHasLateCancelProtection && !hasLateCancellationPayout && (
        <>
          Shifts cancelled by this workplace <strong>will not have any payout</strong> for the
          scheduled worker.
        </>
      )}
    </Text>
  );
}
